import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import {
  Fullscreen as FocusIcon,
  FullscreenExit as ExitFocusIcon,
} from '@mui/icons-material';

interface FocusModeWrapperProps {
  focusMode: boolean;
  onToggleFocusMode: () => void;
  sidebar?: React.ReactNode;
  toolbar?: React.ReactNode;
  children: React.ReactNode;
}

const FocusModeWrapper: React.FC<FocusModeWrapperProps> = ({
  focusMode,
  onToggleFocusMode,
  sidebar,
  toolbar,
  children,
}) => {
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && focusMode) onToggleFocusMode();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [focusMode, onToggleFocusMode]);

  return (
    <Box sx={{ display: 'flex', height: '100%', position: 'relative', overflow: 'hidden' }}>
      {/* Sidebar — hidden in focus mode */}
      {!focusMode && sidebar}

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Toolbar — hidden in focus mode */}
        {!focusMode && toolbar}

        {/* Content area — always visible */}
        <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          {children}

          {/* Focus mode toggle */}
          <Tooltip title={focusMode ? 'Exit Focus Mode (Esc)' : 'Focus Mode'} placement="left">
            <IconButton
              onClick={onToggleFocusMode}
              size="small"
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' },
                zIndex: 10,
              }}
            >
              {focusMode ? <ExitFocusIcon /> : <FocusIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default FocusModeWrapper;
